import { OfferSubCategory } from '../../../common/enums.js';
import { getProductAvailability } from '../../DeviceDetailsCoCo/deviceDetailsUtilsCoCo.js';
import { PriceType } from '../../HorizontalSubscriptionCard/types.js';
import { calculateFee } from '../../ShoppingBasket/shoppingBasketHelpersCoCo.js';
import { isEppDeviceBasketItem } from './placeOrderUtilsCoCo.js';
import type { ShoppingBasketType } from '../../../common/types/shoppingBasketCoCo.js';
import type { ProductOfferingsResponse } from '../../../generated/api/productOfferingsResponse.js';
import type { PlaceOrderFormDataCoCo } from '../placeOrderTypesCoCo.js';

// ============================================================================
// Session Storage for Card Payment Flow
// ============================================================================

/**
 * Session storage key for card payment data.
 * Stores form data needed for thank you page after returning from Nets.
 */
export const PLACE_ORDER_FORM_DATA_SESSION_KEY = 'delivery-details-for-place-order-card-payment-coco';

/**
 * Data stored in session storage during card payment flow.
 */
export interface StoredPlaceOrderCardPaymentData {
  /** Full form data for thank you page */
  formData: PlaceOrderFormDataCoCo;
  /** Transaction ID from initialize response */
  transactionId: string;
  /** Order ID from initialize response - needed for finalize API */
  orderId: string;
  /** Timestamp for expiry/cleanup */
  timestamp: number;
}

/**
 * Save card payment form data to session storage before redirecting to Nets.
 */
export const saveFormDataToSession = (data: StoredPlaceOrderCardPaymentData): void => {
  sessionStorage.setItem(PLACE_ORDER_FORM_DATA_SESSION_KEY, JSON.stringify(data));
};

/**
 * Retrieve card payment form data from session storage after returning from Nets.
 * Returns null if data is not found or cannot be parsed.
 */
export const getFormDataFromSession = (): StoredPlaceOrderCardPaymentData | null => {
  const stored = sessionStorage.getItem(PLACE_ORDER_FORM_DATA_SESSION_KEY);
  if (!stored) {
    return null;
  }
  try {
    return JSON.parse(stored) as StoredPlaceOrderCardPaymentData;
  } catch {
    return null;
  }
};

/**
 * Clear card payment form data from session storage.
 * Should be called on thank you page after reading the data.
 */
export const clearFormDataFromSession = (): void => {
  sessionStorage.removeItem(PLACE_ORDER_FORM_DATA_SESSION_KEY);
};

// ============================================================================
// Card Payment Eligibility
// ============================================================================

export const isDeviceItemsOnly = (shoppingBasket: ShoppingBasketType): boolean => {
  const items = shoppingBasket.productOfferings || [];
  return (
    items.length > 0 &&
    items.every(
      item => item.productOfferingType?.subcategory === OfferSubCategory.DEVICE_POT && !isEppDeviceBasketItem(item)
    )
  );
};

export const isCardPaymentPossible = (
  shoppingBasket: ShoppingBasketType,
  selectedProductOffers: ProductOfferingsResponse[]
): boolean => {
  const items = shoppingBasket.productOfferings || [];
  if (!items.length) {
    return false;
  }

  if (!isDeviceItemsOnly(shoppingBasket)) {
    return false;
  }

  const recurringCharges = calculateFee(items, PriceType.RECURRING_CHARGE);
  const oneTimeCharges = calculateFee(items, PriceType.ONE_TIME_CHARGE);
  if (recurringCharges !== 0 || oneTimeCharges <= 0) {
    return false;
  }

  return items.every(item => {
    const offer = selectedProductOffers.find(selectedOffer => selectedOffer.id === item.id);
    const availability = getProductAvailability(offer);
    return Boolean(availability?.color && !availability.isBackordered);
  });
};
